import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketDetails, ticketComments } from '../api/ticketApi';
import DOMPurify from 'dompurify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ArrowLeft } from 'lucide-react';

const ViewTicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const didFetchRef = useRef(false);

  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [expandedCommentId, setExpandedCommentId] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: '',
    status: '',
    sendEmail: false,
  });

  console.log(ticket);

  /** Helpers **/
  const toggleComment = (commentId) => {
    setExpandedCommentId((prev) => (prev === commentId ? null : commentId));
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const base64ToBlob = (base64Data) => {
    const [meta, content] = base64Data.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const byteChars = atob(content);
    const byteNumbers = Array.from(byteChars).map((ch) => ch.charCodeAt(0));
    return new Blob([new Uint8Array(byteNumbers)], { type: mime });
  };

  // const stripHtml = (html) => {
  //   const tempDiv = document.createElement("div");
  //   tempDiv.innerHTML = DOMPurify.sanitize(html);
  //   return tempDiv.textContent || tempDiv.innerText || "";
  // };

  /** API **/
  const fetchData = async () => {
    try {
      const response = await ticketDetails(id);
      if (response.message === 'Success') {
        const parsedTicket = JSON.parse(response.tickets);
        setTicket(parsedTicket);
        setFormData({
          title: parsedTicket.title || '',
          description: parsedTicket.description || '',
          category: parsedTicket.category || '',
          priority: parsedTicket.priority_display || '',
          status: parsedTicket.status_display || '',
          sendEmail: false,
        });
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }
  };

  const handleSubmit = async (e) => {
    // alert();
    e.preventDefault();

    // const plainTextComment = stripHtml(comment).trim();
    // console.log(plainTextComment);
    // if (!plainTextComment) {
    //   setErrorMsg("Comment can't be empty.");
    //   return;
    // }

    const isCommentEmpty = (html) => {
      const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
      return !clean.trim();
    };

    if (isCommentEmpty(comment)) {
      setErrorMsg("Comment can't be empty.");
      return;
    }
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const base64Attachments = attachments.map((att) => att.data);
      const response = await ticketComments(
        ticket.id,
        comment,
        base64Attachments,
      );

      if (response.message === 'Success') {
        setSuccessMsg('Comment added successfully!');
        setComment('');
        setAttachments([]);
        await fetchData(); // Refresh without full reload
        setTimeout(() => {
          setSuccessMsg('');
        }, 3000);
      } else {
        setErrorMsg('Unexpected response from server.');
      }
    } catch (error) {
      setErrorMsg('Failed to submit comment.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const allowedAttachmentTypes = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/csv',
  ];

  const handleFileChange = async (e) => {
    const filesArray = Array.from(e.target.files);

    const validFiles = filesArray.filter((file) =>
      allowedAttachmentTypes.includes(file.type),
    );

    if (validFiles.length !== filesArray.length) {
      setAttachmentError(
        'Only .jpeg, .png, .pdf, .doc, .docx, .xlsx, .csv files are allowed.',
      );
    } else {
      setAttachmentError('');
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    const base64Files = await Promise.all(
      validFiles.map(async (file) => ({
        name: file.name,
        data: await convertToBase64(file),
      })),
    );
    setAttachments((prev) => [...prev, ...base64Files]);
    e.target.value = '';
  };

  useEffect(() => {
    if (!didFetchRef.current) {
      didFetchRef.current = true;
      fetchData();
    }
  }, [id]);

  /** UI Render Helpers **/
  const renderAttachments = (files) => (
    <div className="flex flex-wrap gap-2">
      {files.map((file, idx) => (
        <a
          key={idx}
          href={file.file}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-gray-100 max-w-[200px] truncate"
        >
          <img src="/icons/doc-icon.svg" alt="doc" className="w-4 h-4" />
          <span className="truncate">{file.file.split('/').pop()}</span>
        </a>
      ))}
    </div>
  );
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] via-[#eff6ff4d] to-[#eef2ff66] py-8">
      <h1 className="text-center text-xl font-semibold text-[#2c3e66] mb-6">
        Ticket Details
      </h1>

      <div className="max-w-3xl mx-auto px-4">
        {ticket ? (
          <>
            <form onSubmit={handleSubmit}>
              {/* Back */}
              <div className="mb-4">
                <button
                  onClick={() => navigate('/contactsupport')}
                  className="text-xl font-bold text-gray-600 hover:text-black flex items-center gap-1"
                >
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
              </div>

              
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2c3e66] text-white flex items-center justify-center font-semibold">
                      {formData.title?.charAt(0) || 'T'}
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {formData.title}
                      </p>
                    </div>
                  </div>

                  <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                    {formData.status}
                  </span>
                </div>

                {/* Meta */}
                <div className="flex gap-4 text-xs text-gray-500 -mt-4 ml-14 mb-4">
                  <span>Ticket ID: {ticket.id}</span>
                  <span>Priority: {formData.priority}</span>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-semibold mb-4">Description</p>
                  <div className="border rounded-lg p-3 text-sm text-gray-600 bg-gray-50">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(formData.description || '', {
                          ALLOWED_TAGS: [
                            'b',
                            'i',
                            'u',
                            'a',
                            'br',
                            'strong',
                            'em',
                            'p',
                            'ul',
                            'ol',
                            'li',
                          ],
                          ALLOWED_ATTR: ['href', 'target', 'rel'],
                        }),
                      }}
                    />
                  </div>
                </div>

                {/* Attachments */}
                {ticket.attachments?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold mb-1">Attachments</p>
                    {renderAttachments(ticket.attachments)}
                  </div>
                )}

                {/* Support Response */}
                <div className="mb-4">
                  <p className="text-sm font-semibold mb-4">Support Response</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <strong>Support Team:</strong> We are looking into your
                    issue and will get back to you shortly.
                  </div>
                </div>
              </div>

              {/* Add Comment */}
              {ticket.status_display !== 'Closed' && (
                <div className="bg-white mt-4 border rounded-2xl shadow-sm p-5">
                  {successMsg && (
                    <p className="text-green-600 text-sm mb-2">{successMsg}</p>
                  )}

                  <p className="text-sm font-semibold mb-4">Add Comment</p>

                  <div className="bg-white border rounded-lg">
                    <ReactQuill
                      theme="snow"
                      value={comment}
                      onChange={setComment}
                      className="min-h-[120px] custom-quill"
                      placeholder="Type your message here..."
                    />
                  </div>

                  {errorMsg && (
                    <p className="text-red-600 text-sm mt-2">{errorMsg}</p>
                  )}

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1 mt-3 text-xs text-[#606260]">
                      {attachments.map((file, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1 px-2 py-1 max-w-[150px] truncate border rounded"
                        >
                          <a
                            href={URL.createObjectURL(base64ToBlob(file.data))}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 truncate"
                          >
                            <img
                              src="/icons/doc-icon.svg"
                              alt="doc"
                              className="w-4 h-4"
                            />
                            <span className="truncate">{file.name}</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedFiles = attachments.filter(
                                (_, index) => index !== i,
                              );
                              setAttachments(updatedFiles);
                            }}
                            className="ml-1 text-red-500"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* File Upload */}
                  <div className="mt-3 text-[#2563eb]">
                    <label className="flex flex-col cursor-pointer text-sm">
                      <span className="flex items-center gap-2">
                        <img
                          src="/icons/file-attach-icon.svg"
                          alt="Attach"
                          className="w-4 h-4"
                        />
                        Attach File
                      </span>

                      <span
                        className={`text-[10px] leading-tight mt-1 ${
                          attachmentError ? 'text-red-500' : 'text-gray-400'
                        }`}
                      >
                        {attachmentError
                          ? attachmentError
                          : 'Accepted: .jpeg, .jpg, .png, .pdf, .doc, .docx, .xlsx, .csv'}
                      </span>

                      <input
                        type="file"
                        multiple
                        accept=".jpeg,.jpg,.png,.pdf,.doc,.docx,.xlsx,.csv"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Button */}
                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-[#d96a1c] hover:bg-[#c75d14] text-white font-medium rounded-lg transition"
                    >
                      {loading ? 'Sending...' : 'Send Comment'}
                    </button>
                  </div>
                </div>
              )}
            </form>

            {/* Comments Section */}
            <div className="mt-6 bg-white border rounded-2xl shadow-sm">
              <div className="px-5 py-4 border-b">
                <h3 className="text-sm font-semibold text-[#2c3e66]">
                  Comments
                </h3>
              </div>

              <div className="p-5 space-y-4 max-h-[400px] overflow-y-auto">
                {ticket?.comments?.length > 0 ? (
                  ticket.comments.map((c) => (
                    <div
                      key={c.id}
                      className="border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-[#2c3e66] text-white flex items-center justify-center text-sm font-semibold">
                          {!c.commented_by || c.commented_by === 'null'
                            ? 'U'
                            : c.commented_by.charAt(0).toUpperCase()}
                        </div>

                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {!c.commented_by || c.commented_by === 'null'
                              ? 'User'
                              : c.commented_by}
                          </p>
                          <p className="text-xs text-gray-500">
                            {c.created_at || ''}
                          </p>
                        </div>
                      </div>

                      <div
                        className="text-sm text-gray-700 cursor-pointer"
                        onClick={() => toggleComment(c.id)}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            expandedCommentId === c.id
                              ? c.comment
                              : `${c.comment.slice(0, 120)}${
                                  c.comment.length > 120 ? '...' : ''
                                }`,
                            {
                              ALLOWED_TAGS: [
                                'b',
                                'i',
                                'u',
                                'a',
                                'br',
                                'strong',
                                'em',
                                'p',
                                'ul',
                                'ol',
                                'li',
                              ],
                              ALLOWED_ATTR: ['href', 'target'],
                            },
                          ),
                        }}
                      />

                      {c.attachments?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {c.attachments.map((file, index) => (
                            <a
                              key={`${c.id}-${index}`}
                              href={file.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-gray-100 max-w-[200px] truncate"
                            >
                              <img
                                src="/icons/doc-icon.svg"
                                alt="doc"
                                className="w-4 h-4"
                              />
                              <span className="truncate">
                                {file.file.split('/').pop()}
                              </span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No comments available.
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white p-6 border rounded-xl shadow-sm animate-pulse">
            Loading ticket details...
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewTicketDetails;